/**
 * The permission checklist shared by both role editors (Workspace Roles +
 * Helpdesk Roles) so they render identically and can't drift. A bordered card
 * with a subject band per group and one full-width row per permission —
 * friendly action label + a hover description via {@link InfoHint}.
 */

import { Checkbox } from "@cometchat-ai/ui-dashboard";
import { describePermission, permissionActionLabel } from "@cometchat-ai/auth/shared";

import { InfoHint } from "../components/info-hint";

export interface PermissionGroup {
  /** The slug's subject part (`agent`, `conversation`, `data-table`, …). */
  subject: string;
  /** The `subject:action` slugs in this group. */
  permissions: string[];
}

/** Title-case a slug subject for the band header (`data-table` → "Data table"). */
function subjectLabel(subject: string): string {
  if (subject === "kb") return "Knowledge";
  return subject.charAt(0).toUpperCase() + subject.slice(1).replace(/-/g, " ");
}

/** Friendly label for the action verb — subject lives in the band, so the row
 *  only needs the action ("Read", "Reply all", "Approve / deny requests"). */
function actionLabel(slug: string): string {
  const override = permissionActionLabel(slug);
  if (override) return override;
  const action = slug.slice(slug.indexOf(":") + 1);
  return action.charAt(0).toUpperCase() + action.slice(1).replace(/-/g, " ");
}

export function PermissionChecklist({
  groups,
  selected,
  onToggle,
  checkboxTestId,
}: {
  groups: ReadonlyArray<PermissionGroup>;
  selected: ReadonlySet<string>;
  onToggle: (slug: string) => void;
  /** Optional data-testid per checkbox (the helpdesk editor's tests use it). */
  checkboxTestId?: (slug: string) => string;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {groups.map((group, gi) => (
        <div
          key={group.subject}
          className={gi > 0 ? "border-t border-dashed border-border" : undefined}
        >
          <p className="bg-muted px-4 py-2 text-xs font-medium text-muted-foreground">
            {subjectLabel(group.subject)}
          </p>
          <div className="divide-y divide-border">
            {group.permissions.map((perm) => {
              const label = actionLabel(perm);
              const desc = describePermission(perm);
              return (
                <div
                  key={perm}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] transition-colors hover:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)]"
                >
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
                    <Checkbox
                      checked={selected.has(perm)}
                      onCheckedChange={() => onToggle(perm)}
                      className="h-4 w-4 rounded-[5px]"
                      data-testid={checkboxTestId?.(perm)}
                    />
                    <span className="truncate text-foreground">{label}</span>
                  </label>
                  {desc ? <InfoHint label={label}>{desc}</InfoHint> : null}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
